import { ValidationError } from "../../../types/error.ts";
import type { AwsPolicyRequest } from "./accesses.ts";
import { type AwsService, isAwsService } from "./constants.ts";

export const AwsPartitions = ["aws", "aws-us-gov"] as const;
export type AwsPartition = (typeof AwsPartitions)[number];

export const isAwsPartition = (value: any): value is AwsPartition => {
  return AwsPartitions.includes(value);
};

export const policyName = (privilegeSet: string) =>
  privilegeSet.includes("/") ? privilegeSet.split("/").at(-1) : privilegeSet;

export const samlProviderArn = (
  accountId: string,
  awsPartition: string,
  samlProviderName: string
) => `arn:${awsPartition}:iam::${accountId}:saml-provider/${samlProviderName}`;

export const nameFromQualified = (qualifiedRole: string) =>
  qualifiedRole.split("/").pop() || qualifiedRole;

const ARN_PATTERN =
  /^arn:(aws|aws-us-gov|aws-cn|aws-iso|aws-iso-b):(.*):(.*):(.*):(.*)$/;

export const isArn = (value: string) => value.match(ARN_PATTERN);

type BaseArnInfo = {
  partition: AwsPartition;
  region?: string;
  accountId?: string;
  resourceType?: string;
  name?: string;
};

type ValidServiceArnInfo = {
  service: AwsService;
} & BaseArnInfo;

export type ArnInfo = {
  service: string;
} & BaseArnInfo;

/**
 * The general format for an ARN looks like this:
 * arn:partition:service:region:account-id:resource-id
 * arn:partition:service:region:account-id:resource-type/resource-id
 * arn:partition:service:region:account-id:resource-type:resource-id
 *
 * See https://towardsthecloud.com/amazon-resource-names-arns
 *
 * Region and accountId are optional because some services don't require them. E.g. S3 ARN: arn:aws:s3:::p0-test-bucket-1
 *
 * @param validate If true then validates that the parsed service is supported by P0 for AWS resource-based access.
 */
export function arnInfo(arn?: string): ValidServiceArnInfo;
export function arnInfo(arn: string, skipValidation: true): ArnInfo;
export function arnInfo(
  arn?: string,
  skipValidation?: boolean
): ArnInfo | ValidServiceArnInfo {
  const parts = arn?.split(":") || [];
  const partition = parts[1];
  if (!isAwsPartition(partition)) {
    throw new ValidationError(
      "arn",
      { arn },
      `Partition ${partition} is not supported`
    );
  }
  const service = parts[2];
  if (!skipValidation && !isAwsService(service)) {
    throw new ValidationError(
      "arn",
      { arn },
      `Service ${service} is not supported`
    );
  }
  return {
    partition,
    service,
    region: parts[3] === "" ? undefined : parts[3],
    accountId: parts[4] === "" ? undefined : parts[4],
    resourceType:
      parts.length == 7
        ? parts[5] // arn:partition:service:region:account-id:resource-type:resource-id
        : parts[5]?.includes("/")
          ? parts[5]?.split("/")?.[0] // arn:partition:service:region:account-id:resource-type/resource-id
          : undefined, // arn:partition:service:region:account-id:resource-id
    name:
      parts.length == 7
        ? parts[6] // arn:partition:service:region:account-id:resource-type:resource-id
        : parts[5]?.includes("/")
          ? parts[5]?.split("/")?.[1] // arn:partition:service:region:account-id:resource-type/resource-id
          : parts[5] !== ""
            ? parts[5]
            : undefined, // arn:partition:service:region:account-id:resource-id
  };
}

export const getArns = (permission: AwsPolicyRequest["permission"]) => {
  return "arn" in permission ? [permission.arn as string] : permission.arns;
};

/**
 * Parses an HTTPS S3 object URL into bucket and key.
 *
 * Supports:
 * - Virtual-hosted style: https://my-bucket.s3.us-west-2.amazonaws.com/path/to/object
 * - Path style: https://s3.us-west-2.amazonaws.com/my-bucket/path/to/object
 */
export const parseS3BucketAndKeyFromUrl = (
  url: string
): { bucketName: string; key: string } => {
  const s3Url = new URL(url);
  const normalizedPath = s3Url.pathname.replace(/^\/+/, "");
  const host = s3Url.hostname;

  const virtualHostSplitIndex =
    host.indexOf(".s3.") >= 0 ? host.indexOf(".s3.") : host.indexOf(".s3-");
  const bucketFromHost =
    virtualHostSplitIndex > 0 && host.toLowerCase().endsWith(".amazonaws.com")
      ? host.slice(0, virtualHostSplitIndex)
      : "";

  if (bucketFromHost) {
    return {
      bucketName: bucketFromHost,
      key: normalizedPath,
    };
  }

  const pathParts = normalizedPath.split("/");
  return {
    bucketName: pathParts[0] ?? "",
    key: pathParts.slice(1).join("/"),
  };
};
